import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Edit, Trash2, Trophy, Users, Calendar, MapPin, Clock, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEventsPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', event_type: 'tournament', date: '', start_time: '', end_time: '',
    max_participants: 32, entry_fee: 0, skill_levels: ['Beginner', 'Intermediate', 'Advanced'], registration_deadline: ''
  });

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API}/admin/events`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) setEvents(await res.json());
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const saveEvent = async () => {
    try {
      const url = editingEvent ? `${API}/admin/events/${editingEvent.event_id}` : `${API}/admin/events`;
      const res = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) { toast.success(editingEvent ? 'Updated' : 'Created'); setShowModal(false); setEditingEvent(null); resetForm(); fetchEvents(); }
      else { toast.error('Failed'); }
    } catch (error) { toast.error('Error'); }
  };

  const deleteEvent = async (eventId) => {
    if (!confirm('Delete this event?')) return;
    try {
      const res = await fetch(`${API}/admin/events/${eventId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) { toast.success('Deleted'); fetchEvents(); }
    } catch (error) { toast.error('Failed'); }
  };

  const resetForm = () => {
    setForm({ name: '', description: '', event_type: 'tournament', date: '', start_time: '', end_time: '',
      max_participants: 32, entry_fee: 0, skill_levels: ['Beginner', 'Intermediate', 'Advanced'], registration_deadline: '' });
  };

  const getEventTypeBadge = (type) => {
    const types = {
      tournament: { class: isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700', label: 'Tournament' },
      clinic: { class: isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700', label: 'Clinic' },
      social: { class: isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700', label: 'Social' },
      league: { class: isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700', label: 'League' }
    };
    const badge = types[type] || types.tournament;
    return <span className={`px-2 py-1 rounded text-xs font-medium ${badge.class}`}>{badge.label}</span>;
  };

  const getStatusBadge = (status) => {
    const statuses = {
      upcoming: { class: isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700', label: 'Upcoming' },
      ongoing: { class: isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700', label: 'Ongoing' },
      completed: { class: isDarkMode ? 'bg-slate-500/20 text-slate-400' : 'bg-gray-100 text-gray-600', label: 'Completed' },
      cancelled: { class: isDarkMode ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-700', label: 'Cancelled' }
    };
    const badge = statuses[status] || statuses.upcoming;
    return <span className={`px-2 py-1 rounded text-xs font-medium ${badge.class}`}>{badge.label}</span>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Events & Tournaments</h1>
            <p className={`${theme.textMuted} mt-1`}>Manage club events and competitions</p>
          </div>
          {hasRole(['super_admin', 'manager']) && (
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" /> Create Event
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12"><div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div></div>
        ) : events.length === 0 ? (
          <Card className={theme.card}>
            <CardContent className="py-12 text-center">
              <Trophy className={`h-12 w-12 mx-auto mb-4 ${theme.textMuted}`} />
              <p className={theme.textMuted}>No events created yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event) => (
              <Card key={event.event_id} className={theme.card}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex gap-2">{getEventTypeBadge(event.event_type)}{getStatusBadge(event.status)}</div>
                  </div>
                  <h3 className={`font-semibold text-lg mb-2 ${theme.text}`}>{event.name}</h3>
                  <p className={`text-sm mb-4 line-clamp-2 ${theme.textMuted}`}>{event.description}</p>
                  <div className="space-y-2 text-sm">
                    <div className={`flex items-center gap-2 ${theme.text}`}><Calendar className={`h-4 w-4 ${theme.textMuted}`} />{event.date}</div>
                    <div className={`flex items-center gap-2 ${theme.text}`}><Clock className={`h-4 w-4 ${theme.textMuted}`} />{event.start_time} - {event.end_time}</div>
                    <div className={`flex items-center gap-2 ${theme.text}`}><Users className={`h-4 w-4 ${theme.textMuted}`} />{event.current_participants || 0} / {event.max_participants}</div>
                    <div className={`flex items-center gap-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}><DollarSign className="h-4 w-4" />₱{event.entry_fee}</div>
                  </div>
                  {hasRole(['super_admin', 'manager']) && (
                    <div className={`flex gap-2 mt-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                      <Button size="sm" variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => {
                        setEditingEvent(event);
                        setForm({ name: event.name, description: event.description, event_type: event.event_type, date: event.date, start_time: event.start_time, end_time: event.end_time, max_participants: event.max_participants, entry_fee: event.entry_fee, skill_levels: event.skill_levels, registration_deadline: event.registration_deadline });
                        setShowModal(true);
                      }}><Edit className="h-4 w-4 mr-1" /> Edit</Button>
                      <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => deleteEvent(event.event_id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className={`w-full max-w-lg my-8 ${theme.modalBg}`}>
              <CardHeader><CardTitle className={theme.text}>{editingEvent ? 'Edit Event' : 'Create Event'}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label className={theme.text}>Event Name</Label><Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className={theme.input} placeholder="Summer Championship" /></div>
                <div><Label className={theme.text}>Description</Label><textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} className={`w-full h-20 px-3 py-2 rounded-md border resize-none ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} placeholder="Event description..." /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className={theme.text}>Event Type</Label><select value={form.event_type} onChange={(e) => setForm(p => ({ ...p, event_type: e.target.value }))} className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}><option value="tournament">Tournament</option><option value="clinic">Clinic</option><option value="social">Social</option><option value="league">League</option></select></div>
                  <div><Label className={theme.text}>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} className={theme.input} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className={theme.text}>Start Time</Label><Input type="time" value={form.start_time} onChange={(e) => setForm(p => ({ ...p, start_time: e.target.value }))} className={theme.input} /></div>
                  <div><Label className={theme.text}>End Time</Label><Input type="time" value={form.end_time} onChange={(e) => setForm(p => ({ ...p, end_time: e.target.value }))} className={theme.input} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className={theme.text}>Max Participants</Label><Input type="number" value={form.max_participants} onChange={(e) => setForm(p => ({ ...p, max_participants: parseInt(e.target.value) }))} className={theme.input} /></div>
                  <div><Label className={theme.text}>Entry Fee (₱)</Label><Input type="number" value={form.entry_fee} onChange={(e) => setForm(p => ({ ...p, entry_fee: parseFloat(e.target.value) }))} className={theme.input} /></div>
                </div>
                <div><Label className={theme.text}>Registration Deadline</Label><Input type="date" value={form.registration_deadline} onChange={(e) => setForm(p => ({ ...p, registration_deadline: e.target.value }))} className={theme.input} /></div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => { setShowModal(false); setEditingEvent(null); }}>Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={saveEvent}>{editingEvent ? 'Update' : 'Create'}</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEventsPage;
